import React, { useEffect, useState } from 'react';
import { Item, AccountDetail } from '../types';
import {
  getItems, getAccountDetails, syncItemsFromAccount,
  updateItem, deleteItem, setItemMultiSpec, setItemMultiQuantityDelivery
} from '../services/api';
import { Box, RefreshCw, ShoppingBag, Save, X, Eye, EyeOff } from 'lucide-react';
import toast from '../lib/toast';
import { formatPrice } from '../lib/format';
// PG-001：商品列表接入通用分页条 + 搜索框（前端本地过滤，默认每页 10 条）
import { PaginationBar, SearchBox, DEFAULT_PAGE_SIZE } from './Pagination';

/**
 * 商品缩略图：加载失败自动回落到占位图标。
 * 图地址走阿里云图床，万一某个 URL 失效，也不要在列表里留一个浏览器的破图。
 *
 * UI-002：列表由大卡片改为「闲鱼工作台式」表格后，缩略图固定 56×56，
 * 占位图标也相应缩小，避免小方格里塞一个大图标。
 */
const ItemThumb: React.FC<{ src?: string }> = ({ src }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-300">
        <Box className="w-5 h-5" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      className="w-full h-full object-cover"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * 商品价格展示：在通用 formatPrice 之上补齐两位小数。
 *
 * 库里的 item_price 是 '3'、'2.5' 这种裸数字，直接展示会变成「¥3」「¥2.5」，
 * 与闲鱼工作台的「¥3.00」不一致。只对纯数字补零，「面议」这类文本原样保留。
 */
const formatItemPrice = (value?: string): string => {
  const text = formatPrice(value);
  return /^¥\d+(\.\d+)?$/.test(text) ? `¥${Number(text.slice(1)).toFixed(2)}` : text;
};

/**
 * 商品创建时间格式化。
 *
 * 库里的 created_at 由 CURRENT_TIMESTAMP / 触发器写入，是 **UTC**，直接展示会早 8 小时。
 * 这里补上 Z 走本地时区；已经带时区后缀（或本来就是 ISO+offset）的不重复补。
 */
const formatItemTime = (value?: string): string => {
  if (!value) return '-';
  const iso = value.includes('T') ? value : value.replace(' ', 'T');
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return value;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * 行内操作按钮组。
 *
 * 桌面端放在「操作」列（右对齐）；移动端列宽不够，整个操作列隐藏，
 * 改由商品信息单元格下方渲染同一组按钮（左对齐），避免小屏出现横向滚动条。
 * 抽成组件是为了两处共用一份 JSX，不然改一处漏一处。
 */
const RowActions: React.FC<{
  item: Item;
  align?: 'start' | 'end';
  onEdit: () => void;
  onToggleSpec: () => void;
  onToggleQty: () => void;
  onDelete: () => void;
}> = ({ item, align = 'end', onEdit, onToggleSpec, onToggleQty, onDelete }) => (
  <div className={`flex flex-wrap items-center gap-1.5 ${align === 'end' ? 'justify-end' : 'justify-start'}`}>
    <button
      onClick={onEdit}
      className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-bold text-gray-600 transition-colors hover:bg-gray-100"
      title="编辑商品详情（用于发货规则匹配）"
    >
      编辑
    </button>
    <button
      onClick={onToggleSpec}
      className={`rounded-lg border px-2.5 py-1.5 text-xs font-bold transition-colors ${
        item.is_multi_spec
          ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
          : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-100'
      }`}
      title="多规格：同一商品不同规格分别匹配卡密"
    >
      多规格{item.is_multi_spec ? ' · 开' : ''}
    </button>
    <button
      onClick={onToggleQty}
      className={`rounded-lg border px-2.5 py-1.5 text-xs font-bold transition-colors ${
        item.is_multi_qty_ship
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
          : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-100'
      }`}
      title="多数量发货：买家买多件时按数量重复发货"
    >
      多数量{item.is_multi_qty_ship ? ' · 开' : ''}
    </button>
    {item.is_offline ? (
      // 已下架 = 同步链路打的标记，只能靠「再次同步」自动清标（闲鱼侧重新上架才会清）。
      // 这里不做「恢复上架」假按钮，避免点了没反应。
      <span
        className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs font-bold text-gray-400"
        title="已下架商品：数据保留在库里，闲鱼侧重新上架后同步会自动恢复"
      >
        已隐藏
      </span>
    ) : (
      <button
        onClick={onDelete}
        className="rounded-lg border border-red-100 bg-red-50/70 px-2.5 py-1.5 text-xs font-bold text-red-500 transition-colors hover:bg-red-100"
        title="隐藏（数据保留，不影响订单统计）"
      >
        隐藏
      </button>
    )}
  </div>
);

const ItemList: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [editForm, setEditForm] = useState<Partial<Item>>({});
  // ITM-001：已下架商品默认隐藏。同步时会自动打「已下架」标记（只打标记、不删数据），
  // 这里给个开关，用户想看 / 想手动删除时能翻出来。
  const [showOffline, setShowOffline] = useState(false);
  // PG-001：搜索 + 分页状态（前端本地过滤，默认每页 10 条）
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const offlineCount = items.filter(i => i.is_offline).length;
  const visibleItems = showOffline ? items : items.filter(i => !i.is_offline);

  // PG-001：按标题 / 商品ID 搜索（在「是否显示已下架」的可见集合里再过滤）
  const filteredItems = visibleItems.filter(item => {
    if (!searchText.trim()) return true;
    const kw = searchText.toLowerCase().trim();
    return (
      item.item_title?.toLowerCase().includes(kw) ||
      String(item.item_id || '').toLowerCase().includes(kw)
    );
  });
  useEffect(() => { setPage(1); }, [searchText, showOffline]);
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedItems = filteredItems.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => {
    getItems().then(setItems);
    // 只有一个账号时直接带入为默认，省得每次手工选一次
    getAccountDetails().then(list => {
      setAccounts(list);
      if (list && list.length === 1) {
        setSelectedAccount(list[0].id);
      }
    });
  }, []);

  const handleSync = async () => {
      if (!selectedAccount) return toast.warning('请先选择账号');
      setLoading(true);
      try {
        const res: any = await syncItemsFromAccount(selectedAccount);
        await getItems().then(setItems);
        // ITM-001：同步会顺带把已下架/已删除的宝贝打上标记（只标记不删数据）
        const flagged = res?.flagged_count ?? 0;
        toast.success(flagged > 0
          ? `${res?.message || '同步完成'}（${flagged} 个已标记下架，可点「显示已下架」查看）`
          : (res?.message || '同步完成'));
      } catch (e) {
        console.error('同步商品失败:', e);
        toast.error('同步失败，请重试');
      } finally {
        setLoading(false);
      }
  };

  // 统一刷新：任何写操作之后都重新拉一次，保证界面与数据库一致
  const refreshItems = async () => {
    try {
      const list = await getItems();
      setItems(list);
    } catch (error) {
      console.error('刷新商品列表失败:', error);
    }
  };

  const handleEdit = (item: Item) => {
    setSelectedItem(item);
    setEditForm({ ...item });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    try {
      // 【修复】此前只改本地 state，从不调用后端 —— 点保存后界面变了，刷新就打回原形。
      // 后端当前只开放 item_detail 字段（标题/价格来自闲鱼，需改完再同步）。
      await updateItem(selectedItem.cookie_id, selectedItem.item_id, {
        item_detail: editForm.item_detail ?? selectedItem.item_detail ?? ''
      });
      toast.success('商品详情已保存');
      setShowEditModal(false);
      await refreshItems();
    } catch (error: any) {
      console.error('更新商品失败:', error);
      toast.error(`保存失败：${error?.message || '请重试'}`);
    }
  };

  const handleDelete = async (item: Item) => {
    // ITM-002：这里实际是「隐藏」而不是物理删除。原因见 db_manager.delete_item_info 注释：
    // 直接删行会让仪表盘销量排行的商品名退化成纯 item_id，还会让历史订单的自动发货
    // 因「商品不属于当前账号」被跳过。所以弹窗文案必须如实说明是隐藏、数据保留。
    if (!confirm(`确认隐藏商品"${item.item_title}"吗？\n\n数据会保留在库里（不会丢订单统计），只是从列表隐藏，之后可以重新显示。`)) return;
    try {
      await deleteItem(item.cookie_id, item.item_id);
      toast.success('商品已隐藏（数据保留，可重新显示）');
      await refreshItems();
    } catch (error: any) {
      console.error('隐藏商品失败:', error);
      toast.error(`隐藏失败：${error?.message || '请重试'}`);
      await refreshItems();
    }
  };

  // 【移除】「添加商品」功能：商品数据设计上只来自闲鱼账号同步，
  // 后端没有新建接口，此前按钮点击后弹窗 JSX 不存在导致毫无反应。需要新商品请选账号点「同步商品」。

  const toggleMultiSpec = async (item: Item) => {
    const next = !item.is_multi_spec;
    // 乐观更新 + 失败回滚
    setItems(prev => prev.map(i =>
      i.cookie_id === item.cookie_id && i.item_id === item.item_id ? { ...i, is_multi_spec: next } : i
    ));
    try {
      await setItemMultiSpec(item.cookie_id, item.item_id, next);
    } catch (error: any) {
      console.error('切换多规格状态失败:', error);
      setItems(prev => prev.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id ? { ...i, is_multi_spec: !next } : i
      ));
      toast.error(`切换失败：${error?.message || '请重试'}`);
    }
  };

  const toggleMultiQty = async (item: Item) => {
    const next = !item.is_multi_qty_ship;
    setItems(prev => prev.map(i =>
      i.cookie_id === item.cookie_id && i.item_id === item.item_id ? { ...i, is_multi_qty_ship: next } : i
    ));
    try {
      await setItemMultiQuantityDelivery(item.cookie_id, item.item_id, next);
    } catch (error: any) {
      console.error('切换多数量发货状态失败:', error);
      setItems(prev => prev.map(i =>
        i.cookie_id === item.cookie_id && i.item_id === item.item_id ? { ...i, is_multi_qty_ship: !next } : i
      ));
      toast.error(`切换失败：${error?.message || '请重试'}`);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">商品管理</h2>
          <p className="text-gray-500 mt-2 text-sm">监控并管理所有账号下的闲鱼商品。</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 md:justify-end">
            {/* PG-001：搜索商品（标题 / 商品ID），输入即过滤并回到第 1 页 */}
            <SearchBox className="w-full sm:w-auto" value={searchText} onChange={setSearchText} placeholder="搜索商品标题/ID..." />
            {/* ITM-001：已下架商品默认隐藏；有标记时才显示这个开关 */}
            {offlineCount > 0 && (
              <button
                onClick={() => setShowOffline(v => !v)}
                className={`flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-bold border transition-colors ${
                  showOffline
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}
                title="已下架商品只在库里打了标记，数据没删；点这里可以查看并隐藏（数据保留，不影响订单统计）"
              >
                {showOffline ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                显示已下架 ({offlineCount})
              </button>
            )}
            <select
                className="ios-input px-4 py-3 rounded-xl text-sm"
                value={selectedAccount}
                onChange={e => setSelectedAccount(e.target.value)}
            >
                <option value="">选择账号以同步</option>
                {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.nickname}</option>
                ))}
            </select>
            <button
                onClick={handleSync}
                disabled={loading || !selectedAccount}
                className="ios-btn-primary flex items-center gap-2 px-6 py-3 rounded-2xl font-bold shadow-lg shadow-yellow-200 disabled:opacity-50"
            >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? '同步中…' : '同步商品'}
            </button>
        </div>
      </div>

      {/* UI-002：商品列表改为「闲鱼工作台式」表格 —— 一行一个商品、左侧 56×56 小图。
          原来的 4 列大图卡片在商品多的时候一屏只能看 4 个，且大图反而抢走标题/价格的注意力。
          列设计按用户确认：只保留 商品信息 / 价格 / 状态 / 创建时间 / 操作（库存与销量两个字段
          本系统拿不到真实值，宁缺不假，直接不放这两列）。 */}
      <div className="relative">
        <div className={`ios-card overflow-hidden rounded-2xl border border-gray-100 bg-white transition-opacity ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm md:min-w-[820px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/70 text-[13px] font-bold text-gray-500">
                  <th className="px-5 py-3 text-left font-bold">商品信息</th>
                  <th className="w-[88px] px-4 py-3 text-left font-bold md:w-[110px]">价格</th>
                  <th className="hidden w-[96px] px-4 py-3 text-left font-bold md:table-cell">状态</th>
                  <th className="hidden w-[150px] px-4 py-3 text-left font-bold md:table-cell">创建时间</th>
                  <th className="hidden w-[280px] px-5 py-3 text-right font-bold md:table-cell">操作</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(item => (
                  <tr
                    key={`${item.cookie_id}-${item.item_id}`}
                    className={`border-b border-gray-50 last:border-0 transition-colors hover:bg-gray-50/80 ${item.is_offline ? 'bg-gray-50/40' : ''}`}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-start gap-3">
                        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                          {/* item_image 由后端返回（采集时入库，老数据从 item_raw_data 里提取）；
                              加载失败会回落到占位图标，不再出现浏览器破图 */}
                          <ItemThumb key={item.item_image || 'empty'} src={item.item_image} />
                        </div>
                        <div className="min-w-0">
                          <div className={`line-clamp-2 text-[13.5px] font-semibold leading-snug ${item.is_offline ? 'text-gray-500' : 'text-gray-900'}`}>
                            {item.item_title}
                          </div>
                          <div className="mt-1 font-mono text-[11.5px] text-gray-400">商品ID {item.item_id}</div>
                          {/* 小屏：操作按钮跟在商品信息下面，避免整表横向溢出 */}
                          <div className="mt-2 md:hidden">
                            <RowActions
                              item={item}
                              align="start"
                              onEdit={() => handleEdit(item)}
                              onToggleSpec={() => toggleMultiSpec(item)}
                              onToggleQty={() => toggleMultiQty(item)}
                              onDelete={() => handleDelete(item)}
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {/* item_price 里可能已自带「¥」（采集端历史数据），统一走 formatPrice 兜底，避免出现「¥¥3.88」 */}
                      <span className="font-bold text-gray-900">{formatItemPrice(item.item_price)}</span>
                    </td>
                    <td className="hidden px-4 py-3 md:table-cell">
                      <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] font-bold ${
                        item.is_offline
                          ? 'bg-gray-200 text-gray-600'
                          : 'bg-emerald-50 text-emerald-700'
                      }`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${item.is_offline ? 'bg-gray-400' : 'bg-emerald-500'}`} />
                        {item.is_offline ? '已下架' : '在售'}
                      </span>
                    </td>
                    <td className="hidden whitespace-nowrap px-4 py-3 text-xs text-gray-500 md:table-cell">{formatItemTime(item.created_at)}</td>
                    <td className="hidden px-5 py-3 md:table-cell">
                      <RowActions
                        item={item}
                        onEdit={() => handleEdit(item)}
                        onToggleSpec={() => toggleMultiSpec(item)}
                        onToggleQty={() => toggleMultiQty(item)}
                        onDelete={() => handleDelete(item)}
                      />
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-20 text-center text-gray-400">
                      <ShoppingBag className="mx-auto mb-4 h-12 w-12 opacity-30" />
                      {searchText.trim() && visibleItems.length > 0
                        ? `没有匹配「${searchText}」的商品`
                        : items.length > 0 && offlineCount > 0 && !showOffline
                          ? `当前没有在售商品（${offlineCount} 个已下架，可点右上角「显示已下架」查看）`
                          : '暂无商品数据，请选择账号进行同步'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40">
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-gray-100 bg-white px-5 py-4 shadow-lg">
              <RefreshCw className="h-7 w-7 animate-spin text-gray-400" />
              <span className="text-sm font-medium text-gray-500">加载中…</span>
            </div>
          </div>
        )}
      </div>

      {/* PG-001：分页条（默认每页 10 条；搜索结果同样分页） */}
      {filteredItems.length > 0 && (
        <div className="ios-card rounded-2xl overflow-hidden shadow-sm border border-gray-100 bg-white">
          <div className="px-4 pt-3 text-sm text-gray-500 font-medium">
            共 <span className="text-gray-900 font-extrabold">{filteredItems.length}</span> 个商品
            {searchText.trim() && (
              <span className="ml-2 text-[11px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md">
                搜索「{searchText}」
              </span>
            )}
          </div>
          <PaginationBar
            total={filteredItems.length}
            page={safePage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        </div>
      )}

      {/* 编辑商品弹窗：此前 setShowEditModal(true) 之后根本没有任何弹窗 JSX，
          点了「编辑」按钮毫无反应。这里补上，且只开放后端真正支持的 item_detail 字段。 */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">编辑商品</h3>
              <button onClick={() => setShowEditModal(false)} className="p-1 text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-3 text-sm text-gray-600">
              <div className="truncate">商品：{selectedItem.item_title}</div>
              <div className="text-xs text-gray-400">ID: {selectedItem.item_id}</div>
            </div>

            <label className="mb-1 block text-xs font-bold text-gray-500">商品详情（用于发货规则匹配）</label>
            <textarea
              className="ios-input mb-3 h-40 w-full rounded-xl p-3 text-sm"
              value={typeof editForm.item_detail === 'string' ? editForm.item_detail : ''}
              onChange={e => setEditForm({ ...editForm, item_detail: e.target.value })}
              placeholder="填写商品详情，发货规则会按「标题 + 详情」匹配关键词"
            />
            <p className="mb-4 text-xs text-gray-400">标题与价格来自闲鱼，请在闲鱼侧修改后点「同步商品」更新。</p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-bold hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> 保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemList;
